import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'hwl_cart_v2';

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setItems(JSON.parse(stored));
    } catch (e) { console.error('Failed to load cart from localStorage', e); }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Add item with optional variant + extras (e.g., gift-card recipient email)
  const addItem = useCallback((product, qty = 1, variant = null, extras = {}) => {
    const lineId = variant ? `${product.id}--${variant.id}` : product.id;
    const price = variant ? variant.price : product.price;
    const title = variant ? `${product.title} \u2014 ${variant.label}` : product.title;

    setItems(prev => {
      const existing = prev.find(i => i.lineId === lineId && JSON.stringify(i.extras || {}) === JSON.stringify(extras));
      if (existing) {
        return prev.map(i => i.lineId === lineId ? { ...i, qty: i.qty + qty } : i);
      }
      return [...prev, {
        lineId,
        id: product.id,
        variantId: variant?.id || null,
        title,
        price,
        image: product.images[0],
        qty,
        extras
      }];
    });
    setDrawerOpen(true);
  }, []);

  const removeItem = useCallback((lineId) => {
    setItems(prev => prev.filter(i => i.lineId !== lineId));
  }, []);

  const updateQty = useCallback((lineId, qty) => {
    if (qty <= 0) {
      setItems(prev => prev.filter(i => i.lineId !== lineId));
      return;
    }
    setItems(prev => prev.map(i => i.lineId === lineId ? { ...i, qty } : i));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clearCart, subtotal, count, drawerOpen, setDrawerOpen }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
